import {
  SafeUserGeneralProfile,
  UserGeneralProfile,
} from "@jugl-web/rest-api/users";
import {
  TFunction,
  getImagePathForSharedLibrary,
  useTranslations,
} from "@jugl-web/utils";
import { useEffect, useMemo, useState } from "react";
import { useUserProfileProviderContext } from "../../providers/UserProfileProvider";
import juglLogo from "./assets/jugl-logo.webp";

interface ProduceSafeProfileOptions {
  t: TFunction;
  isLoading: boolean;
}

export const produceSafeProfile = ({
  t,
  isLoading,
}: ProduceSafeProfileOptions): SafeUserGeneralProfile => ({
  displayName: isLoading
    ? t({ id: "common.jugl-user", defaultMessage: "Jugl User" })
    : t({
        id: "common.former-user",
        defaultMessage: "Former User",
      }),
  avatar: getImagePathForSharedLibrary(juglLogo),
});

export interface UseUserGeneralProfileOptions {
  entityId: string | undefined;
  userId: string | undefined;
  skip?: boolean;
}

export const useUserGeneralProfile = ({
  entityId,
  userId,
  skip = false,
}: UseUserGeneralProfileOptions) => {
  const { requestUserProfile, getCachedUserProfile, watchProfileUpdates } =
    useUserProfileProviderContext();

  const [profile, setProfile] = useState<UserGeneralProfile | null>(() => {
    if (!userId || !entityId) {
      return null;
    }

    return getCachedUserProfile(entityId, userId);
  });

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslations();

  const safeProfile = useMemo<SafeUserGeneralProfile>(() => {
    if (profile) {
      return {
        displayName: profile.displayName,
        avatar: profile.avatar || null,
      };
    }

    return produceSafeProfile({ t, isLoading });
  }, [isLoading, profile, t]);

  // Request user profile from the provider
  useEffect(() => {
    if (!entityId || !userId || skip) {
      return undefined;
    }

    let isAborted = false;

    setIsLoading(true);

    requestUserProfile({ entityId, userId })
      .then((response) => {
        if (!isAborted) setProfile(response.profile);
      })
      .catch(() => {
        // Just swallow the error
      })
      .finally(() => {
        if (!isAborted) setIsLoading(false);
      });

    return () => {
      isAborted = true;
    };
  }, [entityId, requestUserProfile, skip, userId]);

  // Keep the profile up to date whenever it changes
  useEffect(() => {
    if (!entityId || !userId || skip) {
      return undefined;
    }

    const subscription = watchProfileUpdates(
      { entityId, userId },
      (updatedProfile) => {
        setProfile(updatedProfile);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [entityId, skip, userId, watchProfileUpdates]);

  return {
    profile,
    safeProfile,
    isLoading,
  };
};
